import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument } from '../firebase';
import RoleSignupList from './RoleSignupList';
import LoadingChip from './LoadingChip';
import { UtensilsCrossed } from 'lucide-react';

// Trip nights eligible for a dinner signup (excludes the final night before checkout)
const DINNER_DAY_ORDER = ["Thursday 1/7", "Friday 1/8", "Saturday 1/9", "Sunday 1/10", "Monday 1/11", "Tuesday 1/12", "Wednesday 1/13"];

export default function DinnerDishes({ users = [] }) {
  const [dinners, setDinners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signingUpId, setSigningUpId] = useState(null);
  const [signingUpRole, setSigningUpRole] = useState(""); // chef or washer
  const [userNameInput, setUserNameInput] = useState("");
  const [editingMenuId, setEditingMenuId] = useState(null);

  // Form states for menu edit
  const [editType, setEditType] = useState("cooking");
  const [editTheme, setEditTheme] = useState("");
  const [editMenu, setEditMenu] = useState("");

  // Form states for adding a new dinner night
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDay, setNewDay] = useState("");
  const [newType, setNewType] = useState("cooking"); // "cooking" or "restaurant"
  const [newTheme, setNewTheme] = useState("");
  const [newMenu, setNewMenu] = useState("");

  // A dinner night predating this field defaults to "cooking" for backward compatibility
  const getDinnerType = (dinner) => dinner.type === "restaurant" ? "restaurant" : "cooking";

  useEffect(() => {
    // Subscribe to Dinners database
    const unsubscribe = subscribeToCollection("dinners", (data) => {
      // Sort day-by-day chronologically
      const sorted = [...data].sort((a, b) => DINNER_DAY_ORDER.indexOf(a.day) - DINNER_DAY_ORDER.indexOf(b.day));

      setDinners(sorted);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Deep-link handler: scroll to and highlight a specific dinner via sessionStorage
  useEffect(() => {
    if (dinners.length === 0) return;
    const targetId = sessionStorage.getItem('dayones_focus_dinner');
    if (!targetId) return;
    sessionStorage.removeItem('dayones_focus_dinner');
    setTimeout(() => {
      const el = document.getElementById(`dinner-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s ease, outline 0.3s ease';
        el.style.outline = '2px solid var(--color-accent)';
        el.style.boxShadow = '0 0 0 4px rgba(30, 61, 43, 0.2)';
        setTimeout(() => {
          el.style.outline = '';
          el.style.boxShadow = '';
        }, 2200);
      }
    }, 80);
  }, [dinners]);

  const getNamesList = (str) => {
    if (!str) return [];
    return str.split(',').map(n => n.trim()).filter(Boolean);
  };

  const handleStartSignup = (dinnerId, role) => {
    setSigningUpId(dinnerId);
    setSigningUpRole(role);
    const savedName = localStorage.getItem("dayones_user_name") || (users[0]?.id || "");
    setUserNameInput(savedName);
  };

  const handleConfirmSignup = async (dinner) => {
    if (!userNameInput.trim()) return alert("Please enter your name.");

    const currentRoleValue = dinner[signingUpRole] || "";
    const names = getNamesList(currentRoleValue);
    const newName = userNameInput.trim();
    if (!names.includes(newName)) {
      names.push(newName);
    }

    const updated = {
      ...dinner,
      [signingUpRole]: names.join(", ")
    };

    // Removed localStorage mutation
    await saveDocument("dinners", dinner.id, updated);
    setSigningUpId(null);
  };

  const handleResign = async (dinner, role, nameToRemove) => {
    if (!window.confirm(`Are you sure you want to remove this person from this role?`)) return;
    const currentRoleValue = dinner[role] || "";
    const names = getNamesList(currentRoleValue);
    const updatedNames = names.filter(n => n !== nameToRemove);

    const updated = {
      ...dinner,
      [role]: updatedNames.join(", ")
    };
    await saveDocument("dinners", dinner.id, updated);
  };

  const handleEditMenu = (dinner) => {
    setEditingMenuId(dinner.id);
    setEditType(getDinnerType(dinner));
    setEditTheme(dinner.theme);
    setEditMenu(dinner.menu);
  };

  const handleSaveMenu = async (dinner) => {
    const updated = {
      ...dinner,
      type: editType,
      theme: editTheme.trim(),
      menu: editMenu.trim()
    };
    await saveDocument("dinners", dinner.id, updated);
    setEditingMenuId(null);
  };

  const availableDays = DINNER_DAY_ORDER.filter(day => !dinners.some(d => d.day === day));

  const handleStartAddDinner = () => {
    setNewDay(availableDays[0] || "");
    setNewType("cooking");
    setNewTheme("");
    setNewMenu("");
    setShowAddForm(true);
  };

  const handleAddDinner = async (e) => {
    e.preventDefault();
    if (!newDay) {
      alert("Please choose a day for this dinner.");
      return;
    }

    const newDinner = {
      id: `dinner_${Date.now()}`,
      day: newDay,
      type: newType,
      theme: newTheme.trim(),
      menu: newMenu.trim(),
      chef: "",
      washer: "",
      attendees: ""
    };

    await saveDocument("dinners", newDinner.id, newDinner);
    setShowAddForm(false);
    setNewDay("");
    setNewType("cooking");
    setNewTheme("");
    setNewMenu("");
  };

  return (
    <div className="tab-panel">
      {/* Normalize all styles to Montserrat body font */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .dinner-card, .dinner-day-label, .dinner-theme-name, .dinner-menu-details, .role-badge, .role-assignee, .btn-role-signup, .input-field, select, button {
          font-family: var(--font-body) !important;
        }
      `}} />

      <div className="view-title-container">
        <div className="view-title">
          <h2>
            <UtensilsCrossed className="subpage-header-icon" strokeWidth={1.75} />
            Dinner Signup
          </h2>
        </div>
      </div>

      {!isLoading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          {availableDays.length > 0 && (
            <button
              className="btn-primary"
              onClick={() => { if (!showAddForm) { handleStartAddDinner(); } else { setShowAddForm(false); } }}
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            >
              {showAddForm ? "Cancel" : "+ Add Dinner"}
            </button>
          )}
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddDinner} className="glass-panel" style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', color: 'var(--color-text-main)', margin: 0 }}>Schedule a Dinner Night</h3>

          <div className="form-group">
            <label>Dinner Type</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className={newType === "cooking" ? "btn-primary" : "btn-secondary"}
                style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                onClick={() => setNewType("cooking")}
              >
                🍳 Home Cooked
              </button>
              <button
                type="button"
                className={newType === "restaurant" ? "btn-primary" : "btn-secondary"}
                style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                onClick={() => setNewType("restaurant")}
              >
                🍽️ Restaurant
              </button>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Day of Trip</label>
              <select className="input-field" value={newDay} onChange={e => setNewDay(e.target.value)} required>
                {availableDays.map(d => (
                  <option key={d} value={d} style={{ background: '#242220' }}>{d}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{newType === "restaurant" ? "Restaurant Name (optional)" : "Theme / Dinner Title (optional)"}</label>
              <input
                type="text"
                className="input-field"
                placeholder={newType === "restaurant" ? "e.g. La Placita de Santurce" : "e.g. Taco Night"}
                value={newTheme}
                onChange={e => setNewTheme(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>{newType === "restaurant" ? "Reservation Details (optional)" : "Menu Details (optional)"}</label>
            <textarea
              className="input-field"
              placeholder={newType === "restaurant" ? "e.g. 7:30pm for 6, 123 Calle Fortaleza" : "e.g. Carne asada, grilled veggies, churros"}
              value={newMenu}
              onChange={e => setNewMenu(e.target.value)}
              rows="3"
            />
          </div>

          <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>Schedule Dinner</button>
        </form>
      )}

      {isLoading ? (
        <LoadingChip text="Syncing Dinner Board..." />
      ) : dinners.length === 0 ? (
        <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', width: '100%' }}>
          🍽️ Dinner schedule is empty! Click "+ Add Dinner" above to schedule the first night.
        </div>
      ) : (
        <div className="dinner-timeline">
          {dinners.map(dinner => {
            const isSigningUp = signingUpId === dinner.id;
            const isEditingMenu = editingMenuId === dinner.id;

            const dinnerType = getDinnerType(dinner);
            const isRestaurant = dinnerType === "restaurant";

            return (
              <div key={dinner.id} id={`dinner-${dinner.id}`} className="dinner-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span className="dinner-day-label">{dinner.day}</span>
                  <span style={{ fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: isRestaurant ? 'var(--color-info)' : 'var(--color-secondary)' }}>
                    {isRestaurant ? "🍽️ Restaurant" : "🍳 Home Cooked"}
                  </span>
                </div>

                {isEditingMenu ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className={editType === "cooking" ? "btn-primary" : "btn-secondary"}
                        style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                        onClick={() => setEditType("cooking")}
                      >
                        🍳 Home Cooked
                      </button>
                      <button
                        type="button"
                        className={editType === "restaurant" ? "btn-primary" : "btn-secondary"}
                        style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                        onClick={() => setEditType("restaurant")}
                      >
                        🍽️ Restaurant
                      </button>
                    </div>
                    <input
                      type="text"
                      className="input-field"
                      value={editTheme}
                      onChange={e => setEditTheme(e.target.value)}
                      placeholder={editType === "restaurant" ? "Restaurant Name" : "Theme/Dinner Title"}
                    />
                    <textarea
                      className="input-field"
                      value={editMenu}
                      onChange={e => setEditMenu(e.target.value)}
                      placeholder={editType === "restaurant" ? "Reservation details (time, address, link)" : "Menu descriptions"}
                      rows="3"
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn-primary" onClick={() => handleSaveMenu(dinner)}>Save</button>
                      <button className="btn-secondary" onClick={() => setEditingMenuId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="dinner-menu-box" onClick={() => handleEditMenu(dinner)} title="Click to edit">
                    <div className="dinner-theme-name">{dinner.theme || (isRestaurant ? "Restaurant TBD (Click to Edit)" : "TBD (Click to Edit)")}</div>
                    <div className="dinner-menu-details">{dinner.menu || (isRestaurant ? "Click to add reservation details..." : "Click to add menu details...")}</div>
                    <span style={{ fontSize: '0.65rem', color: 'var(--color-primary)', display: 'block', marginTop: '8px', fontWeight: '600' }}>
                      {isRestaurant ? "Click to edit reservation details" : "Click to edit menu details"}
                    </span>
                  </div>
                )}

                <div className="dinner-roles-section" style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                  {/* Restaurant nights don't need a chef, clean-up crew, or a
                      "who's going" signup — the reservation itself covers
                      attendance, so this section is chef/cleanup only. */}
                  {!isRestaurant && (
                    <>
                      {/* Chef Role */}
                      <RoleSignupList
                        label="👨‍🍳 Chefs"
                        names={getNamesList(dinner.chef)}
                        isSigningUp={isSigningUp && signingUpRole === "chef"}
                        userNameInput={userNameInput}
                        onUserNameChange={setUserNameInput}
                        onStartSignup={() => handleStartSignup(dinner.id, "chef")}
                        onConfirmSignup={() => handleConfirmSignup(dinner)}
                        onCancelSignup={() => setSigningUpId(null)}
                        onLeave={(name) => handleResign(dinner, "chef", name)}
                        users={users}
                        buttonLabel="+ Sign Up"
                        borderColor="rgba(var(--color-primary-rgb), 0.18)"
                        accentColor="var(--color-primary)"
                      />

                      {/* Washer/Clean-up Role */}
                      <RoleSignupList
                        label="🧽 Clean-up"
                        names={getNamesList(dinner.washer)}
                        isSigningUp={isSigningUp && signingUpRole === "washer"}
                        userNameInput={userNameInput}
                        onUserNameChange={setUserNameInput}
                        onStartSignup={() => handleStartSignup(dinner.id, "washer")}
                        onConfirmSignup={() => handleConfirmSignup(dinner)}
                        onCancelSignup={() => setSigningUpId(null)}
                        onLeave={(name) => handleResign(dinner, "washer", name)}
                        users={users}
                        buttonLabel="+ Sign Up"
                        borderColor="rgba(198, 138, 53, 0.18)"
                        accentColor="var(--color-secondary)"
                      />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
